/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { putSessionToWorkerAction } from '@root/features/shared-worker/atoms'
import { PlatformUtil } from '@root/shared/core'
import { reatomRequest } from '@root/shared/lib'

import { StorageSession } from '../model'
import { SecureStorageError } from '../secure-storage-error'

import { loadDeviceWalletsAction } from './reatom-device-wallets'

import type { DelugeId } from '@root/entities/user-short/model'

export const storageSessionAtom = atom(
  (ctx) => ctx.spy(storageSessionStateAtom).value,
  'storageSessionAtom',
)

const loadSecureStorageSessionAction = action(
  async (ctx, delugeId: DelugeId) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)

    // A browser has no keychain, and does not need one.
    //
    // Key storage on the device is the desktop application's: it holds the
    // keys it signs with. Everything else signs through the server, which
    // keeps the keys in the signer — so here there is nothing to initialise
    // and nothing to unlock, and saying "unlocked, holding nothing" is the
    // truth rather than a way around the screen. Asking a browser for a
    // keychain is what produced "storage locked" with no way forward.
    if (!PlatformUtil.is('tauri')) {
      return Result.succeed(new StorageSession.Unlocked({ walletsCount: 0 }))
    }

    const stateResult = await ctx.schedule(() =>
      secureStorageService.session(delugeId),
    )

    if (Result.isFailure(stateResult)) {
      return Result.fail(SecureStorageError.fromUnknown(stateResult.failure))
    }

    const state = StorageSession.fromDto(stateResult.success)
    if (state instanceof StorageSession.Unlocked) {
      putSessionToWorkerAction(ctx, { session: stateResult.success })
      await ctx.schedule(() => loadDeviceWalletsAction(ctx))
    }

    return Result.succeed(state)
  },
  'loadSecureStorageSessionAction',
)

export const storageSessionStateAtom = reatomRequest({
  execAction: loadSecureStorageSessionAction,
  name: 'secureStorageStateStateAtom',
})

export const initializeSecureStorageStateAction = action(
  (ctx, delugeId: DelugeId) => {
    if (AsyncStateN.isSuccess(ctx.get(storageSessionStateAtom))) {
      return
    }
    storageSessionStateAtom.execute(ctx, delugeId)
  },
  'initializeSecureStorageStateAction',
)

export const refreshSecureStorageStateAction = action((ctx) =>
  storageSessionStateAtom.refresh(ctx, { strategy: 'success-only' }),
)
